// ================================================================
//  packetDistributionPie
//  Small reusable pie chart showing the 3 mutually-exclusive outcomes
//  of traffic observed by a Biz Interceptor: how many packets were
//  actually captured vs filtered out (didn't match any endpoint rule)
//  vs dropped (buffer full). Used wherever those three counters are
//  already shown as tiles — see environment-status-dialog.html and
//  consolidated-stats-dialog.html — as a visual companion to the raw
//  numbers, not a replacement for them.
//
//  Bindings (one-way, all plain numbers — pass 0/undefined for "no
//  data yet", never pre-round or format):
//    - captured : total_frames_captured
//    - filtered : total_frames_filtered
//    - dropped  : total_frames_dropped
//    - active   : optional — pass a boolean that flips true once this
//                  chart's container becomes visible (e.g. an md-tab that
//                  starts unselected). Chart.js measures the canvas at
//                  construction time; built while its md-tab is still the
//                  inactive one, it gets a 0-size box and renders nothing
//                  (no slices, no legend) even after the tab is later
//                  selected — nothing about becoming visible on its own
//                  re-triggers a layout pass. Omit this binding entirely
//                  for a chart that's already visible when constructed
//                  (e.g. the default-active tab, or no tabs at all).
// ================================================================

angular.module('bizmetryApp').component('packetDistributionPie', {
  bindings: {
    captured: '<',
    filtered: '<',
    dropped: '<',
    active: '<'
  },
  template: `
    <div class="pdp-container" ng-class="{'pdp-container--empty': $ctrl.isEmpty()}">
      <div class="pdp-empty" ng-if="$ctrl.isEmpty()">
        <md-icon>pie_chart_outline</md-icon>
        <span>No traffic captured yet</span>
      </div>
      <canvas class="pdp-canvas" ng-if="!$ctrl.isEmpty()"></canvas>
    </div>
  `,
  controller: function ($element, $timeout) {
    var ctrl = this;
    var chart = null;

    // Same palette role as the tiles right next to this chart: teal for
    // the "good" outcome, muted navy for a neutral/expected exclusion,
    // red for the one outcome that indicates a real problem (buffer
    // overflow) — matches .csd-tile--warn/.esd-tile--warn's own red.
    var COLORS = { captured: '#00BFA5', filtered: '#8E97B4', dropped: '#DC2626' };

    function values() {
      return [ctrl.captured || 0, ctrl.filtered || 0, ctrl.dropped || 0];
    }

    ctrl.isEmpty = function () {
      var v = values();
      return v[0] === 0 && v[1] === 0 && v[2] === 0;
    };

    function render() {
      if (ctrl.isEmpty()) {
        if (chart) { chart.destroy(); chart = null; }
        return;
      }
      var canvas = $element[0].querySelector('canvas');
      if (!canvas) { return; }
      var data = values();

      if (chart) {
        chart.data.datasets[0].data = data;
        chart.update('none');
        return;
      }

      chart = new Chart(canvas.getContext('2d'), {
        type: 'pie',
        data: {
          labels: ['Captured', 'Filtered', 'Dropped'],
          datasets: [{
            data: data,
            backgroundColor: [COLORS.captured, COLORS.filtered, COLORS.dropped],
            borderColor: '#ffffff',
            borderWidth: 2
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 300 },
          plugins: {
            legend: {
              position: 'bottom',
              labels: { boxWidth: 12, padding: 12, font: { size: 11 } }
            },
            tooltip: {
              callbacks: {
                label: function (tooltipItem) {
                  var total = tooltipItem.dataset.data.reduce(function (a, b) { return a + b; }, 0);
                  var value = tooltipItem.parsed;
                  var pct = total ? Math.round((value / total) * 1000) / 10 : 0;
                  return tooltipItem.label + ': ' + value.toLocaleString() + ' (' + pct + '%)';
                }
              }
            }
          }
        }
      });
    }

    // $onChanges fires before $onInit on first binding, when the canvas
    // isn't in the DOM yet (ng-if="!$ctrl.isEmpty()" hasn't rendered it) —
    // the $timeout in $onInit covers that first render; every later
    // $onChanges (auto-refresh tick, tab switch re-binding new numbers)
    // finds the canvas already there and just updates in place.
    ctrl.$onInit = function () {
      $timeout(render, 0);
    };

    ctrl.$onChanges = function (changes) {
      $timeout(render, 0);
      // See the "active" binding's own comment above — a chart already
      // built while hidden needs an explicit resize once it's actually on
      // screen; render() alone won't re-measure the canvas since none of
      // captured/filtered/dropped changed just because the tab was
      // selected.
      if (changes.active && changes.active.currentValue && !changes.active.isFirstChange()) {
        $timeout(function () { if (chart) { chart.resize(); } }, 0);
      }
    };

    ctrl.$onDestroy = function () {
      if (chart) { chart.destroy(); chart = null; }
    };
  }
});
